import {
	HeadContent,
	Scripts,
	ScriptOnce,
	type ErrorComponentProps,
	createRootRouteWithContext,
	retainSearchParams,
	stripSearchParams,
} from '@tanstack/react-router';
import * as v from 'valibot';
import appCssUrl from '../app.css?url';
import type { QueryClient } from '@tanstack/react-query';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import type { PortalLocale } from '@worldcoo/monolith/portals';
import { I18nProvider } from '@lingui/react';
import { type I18n } from '@lingui/core';
import { loadLandingTranslation } from '@/lib/i18n';
import { bootstrapHeadScripts, headPreconnects } from '@/lib/head-bootstrap';
import { lazy, Suspense, useEffect, useState } from 'react';
import { onToasterActivated, flushPendingToasts } from '@/lib/toast-signal';
import {
	getRuntimeAnalyticsCode,
	getRuntimeCdnUrl,
	getRuntimeImgproxyUrl,
	getRuntimeRecaptchaSiteKey,
	getRuntimeAdyenClientKey,
	getRuntimeUsercentricsSettingsId,
	getRuntimeUsercentricsDraft,
} from '@/lib/shared';
import { currentDocumentTrace } from '@/lib/ssr-trace';
import { isCrawlerUserAgent } from '@/lib/crawler-user-agent';
import { loadGtag } from '@worldcoo/ui/lib/analytics';
import {
	ANALYTICS_SERVICE_NAME,
	clearCookiesForRefusedServices,
	loadUsercentrics,
	syncConsentCookie,
	syncUsercentricsBannerVisibility,
	syncUsercentricsLanguage,
	onUsercentricsConsentChanged,
	onUsercentricsReady,
} from '@worldcoo/ui/lib/usercentrics';
import { useBannerFreeRoute } from '@/lib/use-banner-free-route';
import { useUsercentricsServiceConsent } from '@/lib/use-usercentrics-consent';
import type { UsercentricsDecision } from '@worldcoo/ui/lib/consent-cookie';
import { grantEmbeddedConsent } from '@/lib/consent-mode';
import { DefaultCatchBoundary } from './-components/default-catch-boundary';
import { NotFound } from './-components/not-found';

const Toaster = lazy(() => import('@worldcoo/ui/components/sonner').then((m) => ({ default: m.Toaster })));

export interface MyRouterContext {
	queryClient: QueryClient;
	// Request-scoped, so concurrent requests in one worker cannot clobber each other's locale.
	i18n: I18n;
	baseUrl: string;
	portalId: string;
	locale: PortalLocale;
	// Keeps the canonical self-referencing (a canonical matching a non-self hreflang is flagged); in context,
	// never module state, and dehydrated so the first client render agrees (TEC-3607).
	hasLocalePrefix: boolean;
	isBot: boolean;
	isInIframe: boolean;
	// Lets the server render a gated embed as already decided; undefined means undecided. See `syncConsentCookie`.
	consentDecision: UsercentricsDecision | undefined;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	validateSearch: v.object({
		q: v.fallback(v.optional(v.string()), undefined),
		lang: v.fallback(
			v.optional(
				v.picklist([
					'ca',
					'ca-ES',
					'de',
					'de-DE',
					'en',
					'en-EN',
					'en-US',
					'en-GB',
					'es',
					'es-ES',
					'eu',
					'eu-ES',
					'fr',
					'fr-FR',
					'gl',
					'gl-ES',
					'it',
					'it-IT',
					'nl',
					'nl-NL',
					'pt',
					'pt-PT',
					'pt-BR',
				]),
			),
			undefined,
		),
		iframe: v.fallback(v.optional(v.literal(true)), undefined),
		city: v.fallback(v.optional(v.string()), undefined),
	}),
	search: {
		// TODO: retainSearchParams breaks with default values and stripSearchParams, so links pass search={(prev) => prev}.
		middlewares: [stripSearchParams({ q: '' }), retainSearchParams(['lang', 'iframe', 'city'])],
	},
	// The URL's locale can differ from the router's cookie/Accept-Language guess; only the request-scoped
	// instance is reloaded, never the server's global `i18n`. See {@link createLocaleI18n}.
	beforeLoad: async ({ context }) => {
		if (context.i18n.locale !== context.locale) {
			await loadLandingTranslation(context.locale, context.i18n);
		}
	},
	head: () => {
		// Trace ids in the HTML tie the page's later calls, crashes and clicks to the render's trace. Nothing
		// may be appended conditionally: that produced this route's #418 `args[]=HTML` (see `bootstrapHeadScripts`).
		const scripts = bootstrapHeadScripts(
			{
				cdnUrl: getRuntimeCdnUrl(),
				imgproxyUrl: getRuntimeImgproxyUrl(),
				analyticsCode: getRuntimeAnalyticsCode(),
				recaptchaSiteKey: getRuntimeRecaptchaSiteKey(),
				adyenClientKey: getRuntimeAdyenClientKey(),
				usercentricsSettingsId: getRuntimeUsercentricsSettingsId(),
				usercentricsDraft: getRuntimeUsercentricsDraft(),
			},
			currentDocumentTrace(),
		);
		return {
			meta: [
				{
					charSet: 'utf-8',
				},
				{
					name: 'viewport',
					content: 'width=device-width, initial-scale=1',
				},
			],
			scripts: scripts.map((children) => ({ children })),
		};
	},
	errorComponent: RootErrorComponent,
	notFoundComponent: RootNotFound,
	shellComponent: RootDocument,
});

// Through the request-scoped Lingui instance: a concurrent request can flip the server's global mid-render (React #418).
function RootErrorComponent(props: ErrorComponentProps) {
	const { i18n } = Route.useRouteContext();
	return (
		<I18nProvider i18n={i18n}>
			<DefaultCatchBoundary {...props} />
		</I18nProvider>
	);
}

function RootNotFound() {
	const { i18n } = Route.useRouteContext();
	return (
		<I18nProvider i18n={i18n}>
			<NotFound />
		</I18nProvider>
	);
}

function OnDemandToaster() {
	const [show, setShow] = useState(false);
	useEffect(() => onToasterActivated(() => setShow(true)), []);
	if (!show) return null;
	return (
		<Suspense>
			<ToasterWithFlush />
		</Suspense>
	);
}

function ToasterWithFlush() {
	useEffect(() => {
		flushPendingToasts();
	}, []);
	return (
		<Toaster
			className="whitespace-pre-line print:hidden"
			offset={{ top: 'calc(32px + env(safe-area-inset-top))' }}
			mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
			position="top-center"
			theme="light"
		/>
	);
}

function RootDocument({ children }: { children: React.ReactNode }) {
	const { locale, isInIframe } = Route.useRouteContext();
	const bannerFreeRoute = useBannerFreeRoute();
	const cdnUrl = import.meta.env.DEV ? '' : getRuntimeCdnUrl();
	const imgproxyUrl = getRuntimeImgproxyUrl();

	useEffect(() => {
		if (import.meta.env.DEV) {
			void import('react-grab');
		}
	}, []);

	// Loaded only on consent ("basic" Consent Mode, TEC-3817), after hydration. docs/notes/portals/root-route.md
	const analyticsConsent = useUsercentricsServiceConsent(ANALYTICS_SERVICE_NAME);
	useEffect(() => {
		if (analyticsConsent !== 'granted') return;
		loadGtag(getRuntimeAnalyticsCode());
	}, [analyticsConsent]);

	// Never for an embed or a crawler (320 KB, TEC-3747); only the first locale and `bannerFreeRoute` reach
	// the loader. docs/notes/portals/root-route.md
	useEffect(() => {
		if (isInIframe) return;
		if (isCrawlerUserAgent(typeof navigator === 'undefined' ? undefined : navigator.userAgent)) return;
		loadUsercentrics(
			getRuntimeUsercentricsSettingsId(),
			getRuntimeUsercentricsDraft() === 'true',
			locale,
			bannerFreeRoute,
		);
	}, [isInIframe, locale, bannerFreeRoute]);

	// The client-navigation half: arriving from the footer link, the banner is already open and must be closed.
	// Waits for the CMP, which is not fetched yet on mount. See `syncUsercentricsBannerVisibility`.
	useEffect(() => {
		if (isInIframe) return;
		return onUsercentricsReady((available) => {
			if (!available) return;
			void syncUsercentricsBannerVisibility(bannerFreeRoute).catch((error: unknown) => {
				console.error(new Error(`Failed to sync the Usercentrics banner visibility: ${String(error)}`));
			});
		});
	}, [isInIframe, bannerFreeRoute]);

	// No banner in an embed, so nothing else lifts the denied Consent Mode default. See `grantEmbeddedConsent`.
	useEffect(() => {
		if (!isInIframe) return;
		grantEmbeddedConsent();
	}, [isInIframe]);

	// Mirror the decision for the next server render, and delete analytics cookies on refusal: Consent Mode
	// never removes what an earlier acceptance wrote, which would keep the client id for two years.
	useEffect(() => {
		if (isInIframe) return;
		const applyDecision = () => {
			syncConsentCookie();
			clearCookiesForRefusedServices();
		};
		const stopWaiting = onUsercentricsReady((available) => {
			if (available) applyDecision();
		});
		const unsubscribe = onUsercentricsConsentChanged(applyDecision);
		return () => {
			stopWaiting();
			unsubscribe();
		};
	}, [isInIframe]);

	// A locale switch is a router push, never a reload, so the CMP keeps its first language otherwise.
	useEffect(() => {
		void syncUsercentricsLanguage(locale).catch((error: unknown) => {
			console.error(new Error(`Failed to sync the Usercentrics banner language to "${locale}": ${String(error)}`));
		});
	}, [locale]);

	return (
		<html lang={locale}>
			<head>
				<HeadContent />

				{/* Fixed-length and unconditional, for the reason `headPreconnects` documents (TEC-3825). */}
				{headPreconnects({ cdnUrl, imgproxyUrl }).map(({ key, href }) => (
					<link key={key} rel="preconnect" href={href} crossOrigin="anonymous" />
				))}
				<link rel="stylesheet" href={appCssUrl} />
				{/* The `@font-face` and its preload live in `app.css` and in a `Link:` response header, never as
				    interpolated head text: see docs/notes/portals/root-route.md (TEC-3825). */}
			</head>

			<body className="min-h-dvh overflow-y-scroll">
				{/* Moves the parser's stylesheet stall outside every Suspense boundary (TEC-4023). docs/notes/portals/root-route.md */}
				<ScriptOnce>{''}</ScriptOnce>
				{children}
				<OnDemandToaster />

				<div className="hidden sm:block">
					<TanStackDevtools
						plugins={[
							{
								name: 'Tanstack Router',
								render: <TanStackRouterDevtoolsPanel />,
							},
							{
								name: 'Tanstack Query',
								render: <ReactQueryDevtoolsPanel />,
							},
							{
								name: 'Tanstack Form',
								render: <FormDevtoolsPanel />,
							},
						]}
					/>
				</div>
				<Scripts />
			</body>
		</html>
	);
}
